/**
 * Corner notes — the IndexedDB {@link ReflectionStorePort}.
 *
 * One reflection per session, keyed by the session id, so a second reflection
 * for the same session is not a duplicate row: it is the same row. That is why
 * `save` is a replace and why there is no id of its own to collide.
 *
 * **This store deliberately does not cascade** (F-HS-26). Nothing here removes a
 * reflection because its session was deleted. The caller deletes the reflection
 * only after history has verified the session absent from *both* of its stores,
 * so a history delete that failed halfway can never leave a note orphaned away
 * from the effort it describes. A convenience cascade here would be exactly the
 * shortcut that makes that guarantee untrue.
 */

import type {
  EffortSessionId,
  SchemaVersion,
  SessionReflection,
  StoreReadResult,
  WriteOutcome,
} from "@ff/domain";
import { SESSION_REFLECTION_SCHEMA_VERSION, STORE_IDS, schemaVersion } from "@ff/domain";
import type {
  ObservedMutationGate,
  RecordStorePort,
  ReflectionStorePort,
} from "@ff/domain/ports";

import type { RecordDatabase } from "../kernel";
import { canonicalJson, createRecordStore } from "../kernel";
import { decodeSessionReflection, reflectionIndex } from "./payload";

export const SESSION_REFLECTION_VERSION: SchemaVersion = schemaVersion(
  SESSION_REFLECTION_SCHEMA_VERSION,
);

export function createReflectionRecordStore(
  database: RecordDatabase,
): RecordStorePort<SessionReflection> {
  return createRecordStore<SessionReflection>({
    store: STORE_IDS.sessionReflections,
    schemaVersion: SESSION_REFLECTION_VERSION,
    database,
    decodePayload: decodeSessionReflection,
    index: reflectionIndex,
  });
}

export function reflectionLibrary(
  records: RecordStorePort<SessionReflection>,
): ReflectionStorePort {
  return new RecordReflectionStore(records);
}

export function createReflectionStore(database: RecordDatabase): ReflectionStorePort {
  return reflectionLibrary(createReflectionRecordStore(database));
}

class RecordReflectionStore implements ReflectionStorePort {
  readonly #records: RecordStorePort<SessionReflection>;

  constructor(records: RecordStorePort<SessionReflection>) {
    this.#records = records;
  }

  async read(): Promise<StoreReadResult<readonly SessionReflection[]>> {
    const result = await this.#records.read();
    if (result.status !== "loaded") return result;
    // Newest-updated first, which is the order History renders them in. A record
    // store has no inherent order, so leaving it unsorted would let the list
    // reshuffle whenever IndexedDB changed how it enumerated keys.
    const sorted = result.value
      .map((record) => record.value)
      .sort((left, right) => right.updatedAt - left.updatedAt);
    return { ...result, value: sorted };
  }

  async readFor(sessionId: EffortSessionId): Promise<StoreReadResult<SessionReflection>> {
    return this.#records.readRecord(sessionId);
  }

  mutationGate(): ObservedMutationGate {
    return this.#records.mutationGate();
  }

  async save(reflection: SessionReflection): Promise<WriteOutcome> {
    const outcome = await this.#records.commit([
      { kind: "put", key: reflection.sessionId, value: reflection },
    ]);
    if (outcome.status !== "verified") return outcome;

    // F-HS-26 verifies the *reflection* by canonical byte comparison after the
    // read model refreshes, which is a narrower question than the row-level
    // verification the kernel already did inside its transaction.
    const stored = await this.#records.readRecord(reflection.sessionId);
    const expected = canonicalJson(reflection);
    if (stored.status !== "loaded") {
      return { status: "mismatch", expectedCanonical: expected, actualCanonical: null };
    }
    const actual = canonicalJson(stored.value);
    if (actual !== expected) {
      return { status: "mismatch", expectedCanonical: expected, actualCanonical: actual };
    }
    return outcome;
  }

  async clearFor(sessionId: EffortSessionId): Promise<WriteOutcome> {
    // An empty note with no rating is a *clear*, not a save of an empty record:
    // Skip must leave no row behind at all, so there is nothing for a later read
    // to render as saved-but-blank. `makeSessionReflection` returns null for that
    // input, which is what routes the caller here.
    const outcome = await this.#records.commit([{ kind: "remove", key: sessionId }]);
    if (outcome.status !== "verified") return outcome;

    const stored = await this.#records.readRecord(sessionId);
    if (stored.status !== "missing") {
      return { status: "mismatch", expectedCanonical: "", actualCanonical: null };
    }
    return outcome;
  }

  /** Only ever called after both history stores verified empty (F-HS-10). */
  async clearAll(): Promise<WriteOutcome> {
    const current = await this.#records.read();
    if (current.status === "unreadable") {
      return { status: "refused", reason: current.trigger };
    }
    const removals =
      current.status === "loaded"
        ? current.value.map((record) => ({ kind: "remove", key: record.key }) as const)
        : [];
    return this.#records.commit(removals);
  }
}
